/**
 * Shared configuration helpers for entity-specific CDD profile components
 */

import {
	IdentityDocument,
	RegulatoryLicence,
	RiskLevel,
	TrustType,
	VerificationStatus
} from "../../../../types/cdd-records.types";

// ─── Individual Profile helpers ─────────────────────────────────────────────

export const getVerificationStatusClass = (status: VerificationStatus): string => {
	switch (status) {
		case "verified":
			return "bg-green-100 text-green-800 border-green-200";
		case "pending":
			return "bg-yellow-100 text-yellow-800 border-yellow-200";
		case "failed":
			return "bg-red-100 text-red-800 border-red-200";
		case "expired":
			return "bg-orange-100 text-orange-800 border-orange-200";
		case "not-started":
			return "bg-gray-100 text-gray-800 border-gray-200";
		default:
			return "bg-gray-100 text-gray-800 border-gray-200";
	}
};

export const getRiskBadgeClass = (risk?: RiskLevel): string => {
	switch (risk) {
		case "low":
			return "bg-green-100 text-green-800 border-green-200";
		case "medium":
			return "bg-yellow-100 text-yellow-800 border-yellow-200";
		case "high":
			return "bg-orange-100 text-orange-800 border-orange-200";
		case "critical":
			return "bg-red-100 text-red-800 border-red-200";
		default:
			return "bg-gray-100 text-gray-800 border-gray-200";
	}
};

export const getDocumentStatusClass = (status: IdentityDocument["status"]): string => {
	switch (status) {
		case "verified":
			return "bg-green-100 text-green-800";
		case "pending":
			return "bg-yellow-100 text-yellow-800";
		case "rejected":
			return "bg-red-100 text-red-800";
		case "expired":
			return "bg-orange-100 text-orange-800";
		default:
			return "bg-gray-100 text-gray-800";
	}
};

export const documentTypeLabels: Record<string, string> = {
	passport: "Passport",
	"drivers-licence": "Driver's Licence",
	medicare: "Medicare Card",
	"birth-certificate": "Birth Certificate",
	citizenship: "Citizenship Certificate",
	other: "Other Document",
};

// ─── Registered Body helpers ────────────────────────────────────────────────

export const getLicenceStatusClass = (status: RegulatoryLicence["status"]): string => {
	switch (status) {
		case "current":
			return "bg-green-100 text-green-800 border-green-200";
		case "suspended":
			return "bg-yellow-100 text-yellow-800 border-yellow-200";
		case "cancelled":
			return "bg-red-100 text-red-800 border-red-200";
		case "expired":
			return "bg-orange-100 text-orange-800 border-orange-200";
		default:
			return "bg-gray-100 text-gray-800 border-gray-200";
	}
};

// ─── Trust Profile labels ───────────────────────────────────────────────────

export const trustTypeLabels: Record<TrustType, string> = {
	discretionary: "Discretionary Trust",
	unit: "Unit Trust",
	fixed: "Fixed Trust",
	hybrid: "Hybrid Trust",
	bare: "Bare Trust",
	charitable: "Charitable Trust",
	testamentary: "Testamentary Trust",
	superannuation: "Superannuation Fund",
	"managed-investment": "Managed Investment Scheme",
	other: "Other Trust",
};

// ─── Licence Card labels ────────────────────────────────────────────────────

export const licenceTypeLabels: Record<
	string,
	{ label: string; description: string }
> = {
	AFSL: {
		label: "Australian Financial Services Licence",
		description: "Authorizes the holder to carry on a financial services business",
	},
	ACL: {
		label: "Australian Credit Licence",
		description: "Authorizes the holder to engage in credit activities",
	},
	RSE: {
		label: "Registrable Superannuation Entity",
		description: "Registered superannuation fund or approved deposit fund",
	},
	ARSN: {
		label: "Australian Registered Scheme Number",
		description: "Registered managed investment scheme",
	},
	OTHER: {
		label: "Other Regulatory Licence",
		description: "Other regulatory authorisation",
	},
};

// ─── Association Member Card helpers ────────────────────────────────────────

export const positionColors: Record<
	string,
	{ bg: string; text: string; border: string }
> = {
	president: { bg: "bg-blue-100", text: "text-blue-700", border: "border-blue-200" },
	"vice president": { bg: "bg-indigo-100", text: "text-indigo-700", border: "border-indigo-200" },
	secretary: { bg: "bg-emerald-100", text: "text-emerald-700", border: "border-emerald-200" },
	treasurer: { bg: "bg-amber-100", text: "text-amber-700", border: "border-amber-200" },
	chair: { bg: "bg-purple-100", text: "text-purple-700", border: "border-purple-200" },
	default: { bg: "bg-gray-100", text: "text-gray-700", border: "border-gray-200" },
};

export const getPositionColor = (position: string) => {
	const normalizedPosition = position.toLowerCase();
	return positionColors[normalizedPosition] || positionColors.default;
};
